import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";

export default function PlotSelectionModal({ open, onClose, marker, planet }) {
  const [pricing, setPricing] = useState([]);
  const [selected, setSelected] = useState(null);
  const { user, login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    if (planet) {
      api.get(`/pricing?planet=${planet}`).then((r) => setPricing(r.data));
    } else {
      api.get("/pricing").then((r) => setPricing(r.data));
    }
    setSelected(null);
  }, [open, planet]);

  const proceed = () => {
    if (!selected || !marker) return;
    const state = {
      planet,
      marker_id: marker.id,
      marker_label: marker.label,
      lat: marker.lat,
      lon: marker.lon,
      plot_size: selected.size,
      acres: selected.acres,
      amount_inr: selected.price_inr,
    };
    sessionStorage.setItem("teuralink_pending", JSON.stringify(state));
    if (!user) {
      login();
      return;
    }
    navigate("/checkout");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg bg-[#0a0a0c] border-white/10 text-white" data-testid="plot-modal">
        <DialogHeader>
          <div className="overline text-gold">{planet === "moon" ? "Lunar Location" : "Martian Location"}</div>
          <DialogTitle className="font-display text-3xl tracking-tight">{marker?.label}</DialogTitle>
          <DialogDescription className="text-white/60 font-mono-space text-xs">
            LAT {marker?.lat?.toFixed(2)}° · LON {marker?.lon?.toFixed(2)}° · {marker?.available?.toLocaleString?.() || "10,000"} PLOTS AVAILABLE
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 mt-2">
          {pricing.map((p) => (
            <button
              key={p.size}
              onClick={() => setSelected(p)}
              data-testid={`plot-size-${p.size}`}
              className={`text-left p-4 rounded-xl border transition-all ${
                selected?.size === p.size
                  ? "border-[#D4AF37] bg-[#D4AF37]/10"
                  : "border-white/10 bg-white/[0.03] hover:border-white/25"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="font-display text-2xl font-bold">{p.acres}</span>
                {selected?.size === p.size && <Check className="w-4 h-4 text-[#D4AF37]" />}
              </div>
              <div className="text-xs text-white/50 mt-1">{p.acres === 1 ? "ACRE" : "ACRES"}</div>
              <div className="mt-3 font-mono-space text-lg text-gold">₹{p.price_inr.toLocaleString()}</div>
            </button>
          ))}
        </div>

        <div className="mt-4 text-[11px] text-white/45 leading-relaxed">
          Commemorative novelty registration. Physical printed deed shipped by Teuralink admin. Not legal land ownership.
        </div>

        <div className="flex gap-3 mt-4">
          <Button variant="ghost" className="flex-1 text-white hover:bg-white/10" onClick={onClose} data-testid="plot-cancel">Cancel</Button>
          <button
            className="btn-primary flex-1 justify-center"
            disabled={!selected}
            onClick={proceed}
            data-testid="plot-proceed"
            style={{ opacity: selected ? 1 : 0.4 }}
          >
            Proceed to Checkout
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
